"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  LuEye,
  LuEyeOff,
  LuRefreshCcw,
  LuSend,
  LuWallet,
  LuWalletCards,
} from "react-icons/lu";
import { useStartViewTransitionState } from "use-start-view-transition";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { SignUp } from "@/components/shared/signup";
import TradeOnOpenfundLink from "@/components/shared/trade-on-openfund-link";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import {
  UserContext,
  useUserAccountSignalState,
  useUserBalancesSignalState,
} from "@/contexts/user-context";
import { useNumberCounter } from "@/hooks/use-number-counter";
import {
  APP_ROUTES,
  DESO_TICKER_DISPLAY_NAME,
  DESO_TOKEN_DISPLAY_NAME,
  FOCUS_TICKER_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  USDC_DISPLAY_NAME,
} from "@/utils/constants";
import {
  desoToUSD,
  focusToUsd,
  formatDecimalValue,
  formatUSD,
  nanosToInteger,
} from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";

const SidebarBalancesWidget = () => {
  const { refetchUser } = useContext(UserContext);
  const { user, loading: loadingUser } = useUserAccountSignalState();
  const {
    realTimeDesoBalanceNanos,
    realTimeDusdBalanceNanos,
    realTimeFocusUnlockedBalanceNanos,
  } = useUserBalancesSignalState();
  const {
    loading: loadingExchangeRates,
    appState,
    focusExchangeRate,
    refreshExchangeRates,
  } = useFocusMarketData();

  const [refreshing, setRefreshing] = useState(false);

  const [showSidebarAssets, setShowSidebarAssets] =
    useStartViewTransitionState(true);

  const IsLoggedIn = useIsLoggedIn();

  useEffect(() => {
    const savedValue = localStorage.getItem("showSidebarAssets");
    if (savedValue !== null) {
      setShowSidebarAssets(JSON.parse(savedValue));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "showSidebarAssets",
      JSON.stringify(showSidebarAssets),
    );
  }, [showSidebarAssets]);

  const toggleShowAssets = () => {
    setShowSidebarAssets((prev: boolean) => !prev);
  };

  const onRefresh = async () => {
    if (!user) {
      return;
    }

    setRefreshing(true);

    try {
      await Promise.all([refetchUser(), refreshExchangeRates(false)]);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when getting user balances`,
        details: createToastErrorDetails(e),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const loading =
    loadingUser ||
    loadingExchangeRates ||
    !appState ||
    !focusExchangeRate ||
    refreshing;

  const renderPrices = (
    node: (desoExchangeRate: number, focusExchangeRate: number) => ReactNode,
    skeletonClassName?: string,
  ) => {
    return loading ? (
      <Skeleton
        className={cn("mx-auto mr-0 h-4 w-[70px] bg-accent", skeletonClassName)}
      />
    ) : (
      <>{node(appState.USDCentsPerDeSoExchangeRate, focusExchangeRate)}</>
    );
  };

  // Calculate totalUsdValue here
  const totalUsdValue = useMemo(() => {
    if (!appState || !focusExchangeRate) return 0;
    return (
      desoToUSD(
        nanosToInteger(realTimeDesoBalanceNanos),
        appState.USDCentsPerDeSoExchangeRate,
      ) +
      nanosToInteger(realTimeDusdBalanceNanos) +
      focusToUsd(
        nanosToInteger(realTimeFocusUnlockedBalanceNanos),
        focusExchangeRate,
      )
    );
  }, [
    appState,
    focusExchangeRate,
    realTimeDesoBalanceNanos,
    realTimeDusdBalanceNanos,
    realTimeFocusUnlockedBalanceNanos,
  ]);

  // Use the hook at the component level
  const animatedValue = useNumberCounter(totalUsdValue, 2);

  return (
    <div>
      <div
        className="
          z-0 w-full rounded-2xl border border-border-light bg-transparent py-4
        "
      >
        <div className="mb-1 flex items-center justify-between">
          <div
            className="
              mb-2 px-4 text-[10px] font-semibold tracking-widest text-muted
              uppercase
            "
          >
            MARKET (USD)
          </div>
          <div className="relative top-[-2px]">
            <Button
              variant="link"
              className="
                flex items-center gap-2 py-0 text-muted underline-offset-2
                hover:underline
              "
              size="xs"
              disabled={refreshing}
              onClick={onRefresh}
            >
              <LuRefreshCcw />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </div>
        </div>
        <div
          className="
            unforce-radius flex flex-col gap-2 border-b border-border-light pb-4
          "
        >
          <div className="flex w-full items-center justify-between px-4">
            <span
              className="
                flex w-3/5 items-center justify-start gap-2 text-xs
                text-foreground
              "
            >
              <Image
                src="/assets/coin-focus.png"
                width={24}
                height={24}
                alt={FOCUS_TOKEN_DISPLAY_NAME}
                className="rounded-full"
              />
              {FOCUS_TOKEN_DISPLAY_NAME}
            </span>
            <div
              className="
              w-2/5 text-end font-mono text-xs text-muted-foreground
            "
            >
              <button
                type="button"
                className="cursor-pointer bg-transparent p-0 text-inherit"
                onClick={() =>
                  setDialog(DIALOG.WalletTopUp, {
                    open: true,
                  })
                }
              >
                {renderPrices((_, focusExchangeRate) => (
                  <>
                    ~
                    <CoinPriceFormatter price={focusExchangeRate} />
                  </>
                ))}
              </button>
            </div>
          </div>
          <div className="flex w-full items-center justify-between px-4">
            <span
              className="
                flex w-3/5 items-center justify-start gap-2 text-xs
                text-foreground
              "
            >
              <Image
                src="/assets/coin-deso.png"
                width={24}
                height={24}
                alt={DESO_TOKEN_DISPLAY_NAME}
                className="rounded-full"
              />
              {DESO_TOKEN_DISPLAY_NAME}
            </span>
            <div
              className="
              w-2/5 text-end font-mono text-xs text-muted-foreground
            "
            >
              <button
                type="button"
                className="cursor-pointer bg-transparent p-0 text-inherit"
                onClick={() =>
                  setDialog(DIALOG.WalletTopUp, {
                    open: true,
                    params: {
                      reservationCurrency: DESO_TOKEN_DISPLAY_NAME,
                    },
                  })
                }
              >
                {renderPrices(
                  (desoExchangeRate) => `~${formatUSD(desoExchangeRate / 100)}`,
                )}
              </button>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-center gap-2 px-4">
            {IsLoggedIn ? (
              <>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={loading}
                  className="w-full"
                  onClick={() =>
                    setDialog(DIALOG.WalletTopUp, {
                      open: true,
                    })
                  }
                >
                  Buy {FOCUS_TICKER_DISPLAY_NAME}
                </Button>

                <Button
                  variant="outline"
                  size="xs"
                  disabled={loading}
                  className="w-full"
                  onClick={() =>
                    setDialog(DIALOG.WalletTopUp, {
                      open: true,
                      params: {
                        reservationCurrency: DESO_TOKEN_DISPLAY_NAME,
                      },
                    })
                  }
                >
                  Buy {DESO_TICKER_DISPLAY_NAME}
                </Button>
              </>
            ) : (
              <>
                <Link
                  href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${FOCUS_TOKEN_DISPLAY_NAME}`}
                  target="_blank"
                  className="w-full"
                >
                  <Button
                    variant="outline"
                    size="xs"
                    as="div"
                    className="w-full"
                  >
                    Buy {FOCUS_TICKER_DISPLAY_NAME}
                  </Button>
                </Link>

                <Link
                  href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${DESO_TOKEN_DISPLAY_NAME}`}
                  target="_blank"
                  className="w-full"
                >
                  <Button
                    variant="outline"
                    size="xs"
                    as="div"
                    className="w-full"
                  >
                    Buy {DESO_TICKER_DISPLAY_NAME}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {IsLoggedIn ? (
          <>
            <div className="flex items-center justify-between p-4 pb-0">
              <div
                className="
                  text-[10px] font-semibold tracking-widest text-muted uppercase
                "
              >
                ASSETS
              </div>
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="tooltip"
                      onClick={toggleShowAssets}
                      className="
                        text-muted
                        hover:text-foreground
                      "
                      aria-label={
                        showSidebarAssets ? "Hide assets" : "Show assets"
                      }
                    >
                      {showSidebarAssets ? (
                        <LuEyeOff className="size-4" />
                      ) : (
                        <LuEye className="size-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {showSidebarAssets ? "Hide assets" : "Show assets"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {showSidebarAssets && (
              <div className="mt-4 flex flex-col">
                <div className="flex items-start justify-between px-4 pb-1">
                  <span
                    className="
                      flex w-3/5 items-center justify-start gap-2 text-xs
                      text-foreground
                    "
                  >
                    <Image
                      src="/assets/coin-focus.png"
                      width={24}
                      height={24}
                      alt={FOCUS_TOKEN_DISPLAY_NAME}
                      className="rounded-full"
                    />
                    {FOCUS_TOKEN_DISPLAY_NAME}
                  </span>
                  <span
                    className="
                      flex w-2/5 flex-col items-end justify-end text-right
                      font-mono text-xs text-foreground
                    "
                  >
                    {renderPrices(
                      (_, focusExchangeRate) => (
                        <>
                          <span className="text-muted-foreground">
                            ~
                            {formatUSD(
                              focusToUsd(
                                nanosToInteger(
                                  realTimeFocusUnlockedBalanceNanos,
                                ),
                                focusExchangeRate,
                              ),
                            )}{" "}
                          </span>
                          <InfoTooltip
                            text={`${formatDecimalValue(
                              nanosToInteger(realTimeFocusUnlockedBalanceNanos),
                            )} ${FOCUS_TOKEN_DISPLAY_NAME}`}
                          >
                            <span
                              className="
                                inline-block text-muted
                                hover:text-foreground
                              "
                            >
                              {abbreviateNumber(
                                nanosToInteger(
                                  realTimeFocusUnlockedBalanceNanos,
                                ),
                              )}{" "}
                              {FOCUS_TOKEN_DISPLAY_NAME}
                            </span>
                          </InfoTooltip>
                        </>
                      ),
                      "h-8",
                    )}
                  </span>
                </div>

                <div className="flex items-start justify-between px-4 pb-1">
                  <span
                    className="
                      flex w-3/5 items-center justify-start gap-2 text-xs
                      text-foreground
                    "
                  >
                    <Image
                      src="/assets/coin-deso.png"
                      width={24}
                      height={24}
                      alt={DESO_TOKEN_DISPLAY_NAME}
                      className="rounded-full"
                    />
                    {DESO_TOKEN_DISPLAY_NAME}
                  </span>
                  <span
                    className="
                      flex flex-col items-end justify-end text-right font-mono
                      text-xs text-foreground
                    "
                  >
                    {renderPrices(
                      (desoExchangeRate) => (
                        <>
                          <span className="text-muted-foreground">
                            ~
                            {formatUSD(
                              desoToUSD(
                                nanosToInteger(realTimeDesoBalanceNanos),
                                desoExchangeRate,
                              ),
                            )}{" "}
                          </span>
                          <InfoTooltip
                            text={`${formatDecimalValue(
                              nanosToInteger(realTimeDesoBalanceNanos),
                            )} ${DESO_TOKEN_DISPLAY_NAME}`}
                          >
                            <span
                              className="
                                inline-block text-muted
                                hover:text-foreground
                              "
                            >
                              {abbreviateNumber(
                                nanosToInteger(realTimeDesoBalanceNanos),
                              )}{" "}
                              {DESO_TOKEN_DISPLAY_NAME}
                            </span>
                          </InfoTooltip>
                        </>
                      ),
                      "h-8",
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between px-4 pb-1">
                  <span
                    className="
                      flex items-center justify-start gap-2 text-right text-xs
                      text-foreground
                    "
                  >
                    <Image
                      src="/assets/coin-usdc.png"
                      width={24}
                      height={24}
                      alt={USDC_DISPLAY_NAME}
                      className="rounded-full"
                    />
                    {USDC_DISPLAY_NAME}
                  </span>
                  <span
                    className="
                      flex flex-col items-end justify-end font-mono text-xs
                      text-muted-foreground
                    "
                  >
                    {renderPrices(
                      () =>
                        `~${formatUSD(nanosToInteger(realTimeDusdBalanceNanos))}`,
                    )}
                  </span>
                </div>
              </div>
            )}

            {showSidebarAssets && (
              <div>
                <div
                  className="
                    unforce-radius mt-4 flex flex-col items-center
                    justify-center gap-1 border-t border-border-light p-4
                    text-center
                  "
                >
                  <span
                    className="
                      font-shadow-green flex items-center justify-center
                      font-mono text-xl text-emerald-400
                    "
                  >
                    {loading ? (
                      <Skeleton className="h-7 w-[70px] bg-accent" />
                    ) : (
                      animatedValue
                    )}
                  </span>
                  <span
                    className="
                      flex items-center justify-center text-xs text-muted
                    "
                  >
                    Spendable Balance
                  </span>
                </div>

                <div className="mb-2 flex w-full gap-2 px-4">
                  <Button
                    variant="default"
                    size="sm"
                    disabled={loading}
                    className="
                      mt-0 h-10 w-full min-w-0 gap-1.5 px-4 text-sm
                      font-medium
                    "
                    onClick={() =>
                      setDialog(DIALOG.WalletTopUp, {
                        open: true,
                      })
                    }
                  >
                    <LuWalletCards className="size-4 shrink-0" />
                    Top Up
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    disabled={loading}
                    className="
                      mt-0 h-10 w-full min-w-0 gap-1.5 px-4 text-sm
                      font-medium
                    "
                    onClick={() =>
                      setDialog(DIALOG.WalletCashOut, {
                        open: true,
                      })
                    }
                  >
                    <LuWallet className="size-4 shrink-0" />
                    Cashout
                  </Button>
                  <TooltipProvider delayDuration={50}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex">
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={loading}
                            className="
                              mt-0 size-10 justify-center rounded-full
                            "
                            aria-label="Send"
                            onClick={() =>
                              setDialog(DIALOG.WalletSend, {
                                open: true,
                              })
                            }
                          >
                            <LuSend className="size-4.5 shrink-0" />
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">Send</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="mt-4 flex justify-center gap-3 pb-0">
                  <div className="flex items-center">
                    <Link href={APP_ROUTES.wallet}>
                      <Button
                        variant="link"
                        className="
                          flex size-auto h-5 gap-1 bg-transparent p-1 text-xs
                          text-muted
                        "
                      >
                        Wallet{" "}
                      </Button>
                    </Link>
                    <InfoTooltip
                      iconSize={16}
                      text={
                        "You can manage all of your assets, swap between them, and much more on your wallet page"
                      }
                    />
                  </div>

                  <TradeOnOpenfundLink title="Advanced Trading" iconSize={16} />
                </div>
              </div>
            )}
          </>
        ) : (
          <SignUp showBountyHunterLink={false} innerWrapperClassName="mt-4" />
        )}
      </div>
    </div>
  );
};

export default SidebarBalancesWidget;
